import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Users, FileCheck, FileClock, TrendingUp, Search, Eye, LogOut, Loader2, GraduationCap, CheckCircle, FileText, AlertCircle, Send, Headphones } from 'lucide-react';
import { toast } from 'sonner';
import AdminCapacitaciones from './AdminCapacitaciones';
import ThemeToggle from '../components/ThemeToggle';
import AdminAudioStatusBadge from './AdminAudioStatusBadge';
import { canRequestAudioForSubmission, isFormu2Submission, isLegacyAudioEnabled } from './adminAudioEligibility';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function AdminDashboard({ token, user, onLogout, onViewSubmission, onOpenAudio }) {
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [capacitacionFilter, setCapacitacionFilter] = useState('all');
  const [formVariantFilter, setFormVariantFilter] = useState('all');
  const [reportFilter, setReportFilter] = useState('all');
  const [deliveryTab, setDeliveryTab] = useState('pending');
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const [finalizingSubmissionId, setFinalizingSubmissionId] = useState(null);
  const [requestingAudioId, setRequestingAudioId] = useState(null);
  const [updatingSentId, setUpdatingSentId] = useState(null);
  const [activeTab, setActiveTab] = useState('formularios');
  const searchTimerRef = useRef(null);

  // Debounce search input (400ms)
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search]);

  const loadSubmissions = useCallback(async () => {
    if (activeTab !== 'formularios') return;

    setIsLoadingSubmissions(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (capacitacionFilter !== 'all') params.append('capacitacion_id', capacitacionFilter);
      if (formVariantFilter !== 'all') params.append('form_variant', formVariantFilter);
      if (reportFilter !== 'all') params.append('report_status', reportFilter);
      params.append('sent_status', deliveryTab === 'sent' ? 'sent' : 'pending');
      params.append('limit', '500');

      const res = await fetch(`${API_BASE}/api/admin/submissions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setIsLoadingSubmissions(false);
    }
  }, [activeTab, debouncedSearch, statusFilter, capacitacionFilter, formVariantFilter, reportFilter, deliveryTab, token]);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, capRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/admin/capacitaciones`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (capRes.ok) {
        setCapacitaciones(await capRes.json());
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleToggleReportSent = useCallback(async (submission) => {
    const nextSent = !submission.report_sent;
    if (nextSent && submission.report_status !== 'ready') {
      toast.error('No podés marcar como enviado sin informe generado');
      return;
    }

    setUpdatingSentId(submission._id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submission._id}/report-sent`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sent: nextSent })
      });

      if (!res.ok) {
        let message = 'No se pudo actualizar el estado de envío';
        try {
          const errorData = await res.json();
          message = errorData?.detail || message;
        } catch (e) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const data = await res.json();
      setSubmissions((prev) => {
        if ((deliveryTab === 'pending' && data.report_sent) || (deliveryTab === 'sent' && !data.report_sent)) {
          return prev.filter((item) => item._id !== submission._id);
        }
        return prev.map((item) => (
          item._id === submission._id
            ? { ...item, report_sent: data.report_sent, report_sent_at: data.report_sent_at }
            : item
        ));
      });

      setStats((prev) => {
        if (!prev) return prev;
        const sent = prev.reports_sent || 0;
        const pending = prev.reports_pending_send || 0;
        return {
          ...prev,
          reports_sent: Math.max(0, sent + (data.report_sent ? 1 : -1)),
          reports_pending_send: Math.max(0, pending + (data.report_sent ? -1 : 1)),
        };
      });

      toast.success(data.report_sent ? 'Marcado como enviado' : 'Marcado como pendiente');
    } catch (error) {
      toast.error(error.message || 'Error actualizando estado');
    } finally {
      setUpdatingSentId(null);
    }
  }, [deliveryTab, token]);

  const handleFinalizeSubmission = useCallback(async (submission) => {
    if (submission.status === 'finalized') return;

    setFinalizingSubmissionId(submission._id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submission._id}/finalize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        let message = 'No se pudo finalizar el formulario';
        try {
          const errorData = await res.json();
          message = errorData?.detail || message;
        } catch (e) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const data = await res.json();
      const updatedSubmission = data.submission;

      setSubmissions((prev) => {
        if (statusFilter === 'draft') {
          return prev.filter((item) => item._id !== submission._id);
        }
        return prev.map((item) => (
          item._id === submission._id
            ? {
                ...item,
                status: updatedSubmission.status,
                finalized_at: updatedSubmission.finalized_at,
                updated_at: updatedSubmission.updated_at,
              }
            : item
        ));
      });

      setStats((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          finalized: Math.max(0, (prev.finalized || 0) + 1),
          draft: Math.max(0, (prev.draft || 0) - 1),
        };
      });

      toast.success('Formulario marcado como finalizado');
    } catch (error) {
      toast.error(error.message || 'Error al finalizar formulario');
    } finally {
      setFinalizingSubmissionId(null);
    }
  }, [statusFilter, token]);

  const handleRequestAudio = useCallback(async (submission) => {
    if (!canRequestAudioForSubmission(submission)) return;

    setRequestingAudioId(submission._id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submission._id}/request-audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        let message = 'No se pudo generar el link de audio';
        try {
          const errorData = await res.json();
          message = errorData?.detail || message;
        } catch (e) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const data = await res.json();
      let copied = false;
      if (data.audio_url) {
        try {
          await navigator.clipboard.writeText(data.audio_url);
          copied = true;
        } catch (clipboardError) {
          // The link was generated even if the browser blocks clipboard access.
          console.warn('No se pudo copiar el link de audio:', clipboardError);
          window.prompt('Copiá el link de audio', data.audio_url);
        }
      }
      toast.success(copied ? 'Link de audio generado y copiado' : 'Link de audio generado');
      await loadSubmissions();
    } catch (error) {
      toast.error(error.message || 'Error solicitando audio');
    } finally {
      setRequestingAudioId(null);
    }
  }, [loadSubmissions, token]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      // ignore network errors on logout
    }
    localStorage.removeItem('admin_token');
    onLogout();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderReportBadge = (sub) => {
    if (sub.report_status === 'ready') {
      return (
        <Badge variant="default" className="bg-green-600">
          <FileText className="w-3 h-3 mr-1" />
          Listo
        </Badge>
      );
    }

    if (sub.report_status === 'generating') {
      return (
        <Badge variant="secondary">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Generando
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-muted-foreground">
        Sin informe
      </Badge>
    );
  };

  const renderDeliveryControl = (sub) => {
    const isSent = Boolean(sub.report_sent);
    const canToggle = isSent || sub.report_status === 'ready';
    const isUpdating = updatingSentId === sub._id;

    if (!canToggle) {
      return (
        <span className="inline-flex items-center text-xs text-muted-foreground">
          <AlertCircle className="w-3 h-3 mr-1" />
          No listo
        </span>
      );
    }

    return (
      <Button
        size="sm"
        type="button"
        variant={isSent ? 'secondary' : 'outline'}
        onClick={() => handleToggleReportSent(sub)}
        disabled={isUpdating}
        className={isSent ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' : ''}
      >
        {isUpdating ? (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        ) : isSent ? (
          <CheckCircle className="w-3 h-3 mr-1" />
        ) : (
          <Send className="w-3 h-3 mr-1" />
        )}
        {isSent ? 'Enviado' : 'Marcar'}
      </Button>
    );
  };

  const renderActionButtons = (sub) => {
    const isFinalized = sub.status === 'finalized';
    const isFinalizing = finalizingSubmissionId === sub._id;
    const isRequestingAudio = requestingAudioId === sub._id;

    return (
      <div className="flex items-center justify-end gap-2">
        {canRequestAudioForSubmission(sub) && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => handleRequestAudio(sub)}
            disabled={isRequestingAudio}
            data-testid={`request-audio-${sub._id}`}
          >
            {isRequestingAudio ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Headphones className="w-4 h-4 mr-1" />
            )}
            Solicitar audio
          </Button>
        )}
        {!isFinalized && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => handleFinalizeSubmission(sub)}
            disabled={isFinalizing}
            data-testid={`finalize-submission-${sub._id}`}
          >
            {isFinalizing ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-1" />
            )}
            Finalizar
          </Button>
        )}
        <a
          href={`/admin#/submission/${sub._id}`}
          onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); onViewSubmission(sub._id); } }}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent hover:text-accent-foreground h-9 px-3"
          data-testid={`view-submission-${sub._id}`}
        >
          <Eye className="w-4 h-4 mr-1" />
          Ver
        </a>
      </div>
    );
  };

  const isTestSubmission = (sub) => sub.is_test_submission === true;

  const renderSubmissionStatusBadge = (sub) => {
    return (
      <Badge variant={sub.status === 'finalized' ? 'default' : 'secondary'}>
        {sub.status === 'finalized' ? 'Finalizado' : 'En progreso'}
      </Badge>
    );
  };

  const getSubmissionRowClass = (sub) => (
    isTestSubmission(sub)
      ? 'border-l-4 border-l-cyan-500 bg-cyan-50/50 dark:bg-cyan-950/20'
      : ''
  );

  const renderStudentCell = (sub) => (
    <div className="flex min-w-[190px] items-start gap-2">
      {isFormu2Submission(sub) && (
        <Badge
          variant="outline"
          className="mt-0.5 shrink-0 border-teal-300 bg-teal-50 px-2 text-teal-800 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100"
          title="Formulario con audio"
        >
          F2
        </Badge>
      )}
      {!isFormu2Submission(sub) && isLegacyAudioEnabled(sub) && (
        <Badge
          variant="outline"
          className="mt-0.5 shrink-0 border-amber-300 bg-amber-50 px-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
          title="Audio habilitado manualmente para formulario clásico"
        >
          Audio
        </Badge>
      )}
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium leading-5">{sub.user?.name || '-'}</span>
          {isTestSubmission(sub) && (
            <Badge variant="outline" className="border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-100">
              PRUEBA
            </Badge>
          )}
        </div>
        <AdminAudioStatusBadge submission={sub} />
      </div>
    </div>
  );

  // Group submissions by capacitacion for display
  const groupedSubmissions = submissions.reduce((groups, sub) => {
    const capName = sub.capacitacion_name || 'Sin capacitación';
    if (!groups[capName]) {
      groups[capName] = [];
    }
    groups[capName].push(sub);
    return groups;
  }, {});

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary">Admin - Academia Cortex</h1>
            <p className="text-sm text-muted-foreground">Panel de Administración</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onOpenAudio} data-testid="admin-audio-dashboard-button">
              <Headphones className="w-4 h-4 mr-2" />
              Audio
            </Button>
            <ThemeToggle />
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FileCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.finalized}</p>
                    <p className="text-sm text-muted-foreground">Finalizados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <FileClock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.draft}</p>
                    <p className="text-sm text-muted-foreground">En progreso</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.recent_week}</p>
                    <p className="text-sm text-muted-foreground">Última semana</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs for Formularios and Capacitaciones */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="formularios" className="flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              Formularios
            </TabsTrigger>
            <TabsTrigger value="capacitaciones" className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Capacitaciones
            </TabsTrigger>
          </TabsList>

          <TabsContent value="formularios" className="mt-6">
            {/* Submissions Table */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {deliveryTab === 'sent' ? 'Informes Enviados' : 'Formularios Pendientes de Envío'}
                </CardTitle>
                <CardDescription>
                  {deliveryTab === 'sent'
                    ? 'Formularios con informe marcado manualmente como enviado al alumno'
                    : 'Formularios visibles para trabajar y enviar informe al alumno'}
                </CardDescription>
                <Tabs value={deliveryTab} onValueChange={setDeliveryTab} className="mt-3">
                  <TabsList>
                    <TabsTrigger value="pending">
                      Pendientes
                      {typeof stats?.reports_pending_send === 'number' && (
                        <span className="ml-2 text-xs opacity-80">({stats.reports_pending_send})</span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="sent">
                      Enviados
                      {typeof stats?.reports_sent === 'number' && (
                        <span className="ml-2 text-xs opacity-80">({stats.reports_sent})</span>
                      )}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por nombre o email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                      data-testid="admin-search-input"
                    />
                  </div>
                  <Select value={capacitacionFilter} onValueChange={setCapacitacionFilter}>
                    <SelectTrigger className="w-[200px]" data-testid="admin-capacitacion-filter">
                      <SelectValue placeholder="Capacitación" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las capacitaciones</SelectItem>
                      <SelectItem value="none">Sin capacitación</SelectItem>
                      {capacitaciones.map((cap) => (
                        <SelectItem key={cap._id} value={cap._id}>
                          {cap.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={formVariantFilter} onValueChange={setFormVariantFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="admin-form-variant-filter">
                      <SelectValue placeholder="Formulario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="classic">Original</SelectItem>
                      <SelectItem value="formu2">F2 - formulario con audio</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="admin-status-filter">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="finalized">Finalizados</SelectItem>
                      <SelectItem value="draft">En progreso</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={reportFilter} onValueChange={setReportFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="admin-report-filter">
                      <SelectValue placeholder="Informe" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="ready">Con informe</SelectItem>
                      <SelectItem value="pending">Sin informe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Grouped submissions by capacitacion */}
                {capacitacionFilter === 'all' && Object.keys(groupedSubmissions).length > 1 ? (
                  <div className="space-y-6">
                    {Object.entries(groupedSubmissions).map(([capName, subs]) => (
                      <div key={capName} className="border rounded-lg">
                        <div className="bg-muted/50 px-4 py-3 border-b flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{capName}</span>
                          <Badge variant="secondary" className="ml-auto">
                            {subs.length} formulario{subs.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[120px]">Envío</TableHead>
                              <TableHead>Nombre</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Estado</TableHead>
                              <TableHead>Informe</TableHead>
                              <TableHead>Fecha</TableHead>
                              <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {subs.map((sub) => (
                              <TableRow key={sub._id} className={getSubmissionRowClass(sub)}>
                                <TableCell>
                                  {renderDeliveryControl(sub)}
                                </TableCell>
                                <TableCell>{renderStudentCell(sub)}</TableCell>
                                <TableCell>{sub.user?.email || '-'}</TableCell>
                                <TableCell>
                                  {renderSubmissionStatusBadge(sub)}
                                </TableCell>
                    <TableCell>
                      {renderReportBadge(sub)}
                    </TableCell>
                                <TableCell>{formatDate(sub.created_at)}</TableCell>
                                <TableCell className="text-right">
                                  {renderActionButtons(sub)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Single table when filtered
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[120px]">Envío</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Capacitación</TableHead>
                          <TableHead>Estado</TableHead>
                    <TableHead>Informe</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isLoadingSubmissions ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                              <span className="inline-flex items-center">
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Cargando formularios...
                              </span>
                            </TableCell>
                          </TableRow>
                        ) : submissions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                              {deliveryTab === 'sent'
                                ? 'No hay informes marcados como enviados'
                                : 'No hay formularios pendientes para mostrar'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          submissions.map((sub) => (
                            <TableRow key={sub._id} className={getSubmissionRowClass(sub)}>
                              <TableCell>
                                {renderDeliveryControl(sub)}
                              </TableCell>
                              <TableCell>{renderStudentCell(sub)}</TableCell>
                              <TableCell>{sub.user?.email || '-'}</TableCell>
                              <TableCell>
                                <span className="text-sm text-muted-foreground">
                                  {sub.capacitacion_name || 'Sin capacitación'}
                                </span>
                              </TableCell>
                              <TableCell>
                                {renderSubmissionStatusBadge(sub)}
                              </TableCell>
                    <TableCell>
                      {renderReportBadge(sub)}
                    </TableCell>
                              <TableCell>{formatDate(sub.created_at)}</TableCell>
                              <TableCell className="text-right">
                                {renderActionButtons(sub)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="capacitaciones" className="mt-6">
            <AdminCapacitaciones token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default AdminDashboard;
